import { Typography } from '@mui/material';
import { FormikProvider } from 'formik';
import { afip, provinces } from '@shared/helpers/constants';
import { memo } from 'react';

import StyledTextField from '@shared/components/StyledTextField';
import FormSection from '@shared/components/FormSection';
import GenericDropdown from '@shared/components/GenericDropdown';
import ErrorDialogModal from '@shared/components/Dialogs/Error';
import ActionButton from '@shared/components/Buttons/ActionButton';

const BillingData = ({
  formik,
  billingError,
  clearBillingError,
  formikPaymentRef,
  paymentSelectorRef,
  showConfirmButton,
  isLoadingPay,
  isMobile,
}) => {
  return (
    <>
      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          autoComplete="off"
          style={{ width: '100%' }}
        >
          <FormSection
            title="Datos de facturación"
            customLayout={{
              xs: {
                1: 12,
                2: 12,
                3: 7,
                4: 5,
                5: 12,
                6: 4,
                7: 4,
                8: 4,
                9: 8,
                10: 4,
                11: 12,
                13: 12,
              },

              xl: {
                1: 3.5,
                2: 3.5,
                3: 2.8,
                4: 2.2,

                5: 7,
                6: 1.5,
                7: 1.5,
                8: 2,

                9: 7,
                10: 1.5,
                11: 3.5,
                12: 12,
                13: 12,
              },
            }}
          >
            {/* Nombre */}
            <StyledTextField
              label="Nombre"
              name="ownerCardName"
              formik={formik}
            />

            {/* Apellido */}
            <StyledTextField
              label="Apellido"
              name="ownerCardLastName"
              formik={formik}
            />

            {/* Situación Fiscal */}
            <GenericDropdown
              id="afip-dropdown"
              items={afip.map(option => ({
                id: option.id,
                fragments: [{ text: option.name, bold: false }],
                icon: option.icon,
              }))}
              selectedItem={
                afip
                  .filter(opt => opt.id === formik.values.afip)
                  .map(opt => ({
                    id: opt.id,
                    fragments: [{ text: opt.name, bold: false }],
                    icon: opt.icon,
                  }))[0] || null
              }
              onSelect={item => formik.setFieldValue('afip', item.id)}
              buttonLabel="Situación Fiscal"
              hasError={Boolean(formik.touched.afip && formik.errors.afip)}
              helperText={formik.touched.afip && formik.errors.afip}
            />

            {/* CUIT/CUIL */}
            <StyledTextField
              label="CUIT / CUIL"
              name="ownerDocument"
              formik={formik}
            />

            {/* Dirección */}
            <StyledTextField label="Calle" name="street" formik={formik} />
            <StyledTextField
              label={isMobile ? 'N°' : 'Número'}
              name="streetNumber"
              formik={formik}
            />
            <StyledTextField label="Piso" name="floor" formik={formik} />
            <StyledTextField
              label={isMobile ? 'Dpto.' : 'Departamento'}
              name="department"
              formik={formik}
            />
            <StyledTextField label="Ciudad" name="city" formik={formik} />
            <StyledTextField label={'CP'} name="postalCode" formik={formik} />

            {/* Provincia */}
            <GenericDropdown
              id="province-dropdown"
              items={provinces.map(option => ({
                id: option.id,
                fragments: [{ text: option.name, bold: false }],
                icon: option.icon,
              }))}
              selectedItem={
                provinces
                  .filter(opt => opt.id === formik.values.province)
                  .map(opt => ({
                    id: opt.id,
                    fragments: [{ text: opt.name, bold: false }],
                    icon: opt.icon,
                  }))[0] || null
              }
              onSelect={item => formik.setFieldValue('province', item.id)}
              buttonLabel="Provincia"
              hasError={Boolean(
                formik.touched.province && formik.errors.province
              )}
              helperText={formik.touched.province && formik.errors.province}
            />

            {/* Mensaje de validación general */}
            {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
              <Typography
                color="error"
                sx={{ fontSize: '13px', fontWeight: 600 }}
              >
                Verifique que haya ingresado todos los datos del formulario
                antes de continuar con la compra.
              </Typography>
            )}

            {showConfirmButton && (
              <ActionButton
                isLoading={isLoadingPay}
                onClick={async () => {
                  if (isLoadingPay) return;

                  const isPaymentValid = paymentSelectorRef?.current?.validatePayment?.();
                  if (!isPaymentValid) return;

                  if (formikPaymentRef?.current) {
                    await formikPaymentRef.current.submitForm();
                  }

                  formik.submitForm();
                }}
                buttonText="Confirmar"
              />
            )}
          </FormSection>
        </form>
      </FormikProvider>

      {billingError.message && (
        <ErrorDialogModal
          isOpen={billingError.isOpen}
          onClose={clearBillingError}
          title={billingError.title}
          message={billingError.message}
          subText={billingError.subText}
        />
      )}
    </>
  );
};

export default memo(BillingData);
